import { z } from 'zod';
import { CONNECTOR_AUTHS, CONNECTOR_FORMATS, CONNECTOR_INTERVALS, IMPORT_KINDS } from '../domain';

// Import du portefeuille (BA-009 à BA-012, spec 04).

export const importBatchSchema = z.object({
  id: z.string(),
  kind: z.enum(IMPORT_KINDS),
  filename: z.string(),
  status: z.enum(['uploaded', 'mapping', 'validating', 'processing', 'completed', 'failed']),
  dryRun: z.boolean(),
  rowsTotal: z.number().int(),
  rowsOk: z.number().int(),
  rowsError: z.number().int(),
  rowsWarning: z.number().int(),
  /** Fichiers de lignes traités sur le total (progression). */
  progress: z.object({ done: z.number().int(), total: z.number().int() }),
  summary: z.record(z.string(), z.number()),
  detection: z.record(z.string(), z.unknown()).nullable(),
  mapping: z.record(z.string(), z.string()).nullable(),
  hasReport: z.boolean(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  dryRunCompletedAt: z.string().nullable(),
});
export type ImportBatchDto = z.infer<typeof importBatchSchema>;

export const importFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  required: z.boolean(),
});

export const importUploadResponseSchema = z.object({
  batch: importBatchSchema,
  headers: z.array(z.string()),
  /** 20 premières lignes telles que lues. */
  preview: z.array(z.array(z.string())),
  fields: z.array(importFieldSchema),
  suggestedMapping: z.record(z.string(), z.string()),
});

export const importMappingRequestSchema = z.object({
  mapping: z.record(z.string(), z.string()),
  /** Enregistre ce mapping pour les prochains imports du même type. */
  save: z.boolean().optional(),
});

export const importRowPreviewSchema = z.object({
  line: z.number().int(),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  value: z.unknown().nullable(),
});

export const importMappingResponseSchema = z.object({
  batch: importBatchSchema,
  missing: z.array(z.string()),
  preview: z.array(importRowPreviewSchema),
});

export const importRunRequestSchema = z.object({
  /** Simulation : rapport sans écriture, obligatoire au premier import d'un tenant. */
  dryRun: z.boolean(),
  /** Contrats : résilier les contrats en cours absents du fichier (désactivé par défaut). */
  markAbsentAsCancelled: z.boolean().optional(),
});

export const importPageSchema = z.object({
  items: z.array(importBatchSchema),
  nextCursor: z.string().nullable(),
});

// Connecteur SI en lecture seule (BA-094) : lecture planifiée d'une API JSON ou d'un CSV publié.

const httpsUrl = z
  .string()
  .url()
  .max(500)
  .refine((u) => u.startsWith('https://'), 'URL HTTPS requise');
const headerName = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9-]{1,60}$/, 'Nom d’en-tête invalide');
const paramName = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_.[\]-]{1,60}$/, 'Nom de paramètre invalide');

export const connectorInputSchema = z.object({
  kind: z.enum(IMPORT_KINDS),
  format: z.enum(CONNECTOR_FORMATS),
  url: httpsUrl,
  auth: z.enum(CONNECTOR_AUTHS).default('none'),
  headerName: headerName.nullable().optional(),
  /** Jeton, « identifiant:mot de passe » ou clé, selon `auth`. Écrit seulement, jamais relu. */
  secret: z.string().min(1).max(2000).optional(),
  itemsPath: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/, 'Chemin invalide (ex. data.items)')
    .nullable()
    .optional(),
  sinceParam: paramName.nullable().optional(),
  intervalMinutes: z
    .number()
    .int()
    .refine((n) => (CONNECTOR_INTERVALS as readonly number[]).includes(n), 'Intervalle non proposé')
    .default(60),
  mapping: z.record(z.string(), z.string()).nullable().optional(),
  enabled: z.boolean().default(false),
});
export const connectorPatchSchema = connectorInputSchema
  .omit({ kind: true })
  .partial()
  .extend({ secret: z.string().min(1).max(2000).nullable().optional() });

export const connectorSchema = z.object({
  id: z.string(),
  kind: z.enum(IMPORT_KINDS),
  format: z.enum(CONNECTOR_FORMATS),
  url: z.string(),
  auth: z.enum(CONNECTOR_AUTHS),
  headerName: z.string().nullable(),
  hasSecret: z.boolean(),
  itemsPath: z.string().nullable(),
  sinceParam: z.string().nullable(),
  intervalMinutes: z.number().int(),
  mapping: z.record(z.string(), z.string()).nullable(),
  enabled: z.boolean(),
  cursor: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  lastStatus: z.enum(['ok', 'empty', 'error']).nullable(),
  lastError: z.string().nullable(),
  /** Champs obligatoires sans colonne associée : la lecture planifiée reste bloquée. */
  missing: z.array(z.string()),
});
export const connectorListSchema = z.object({ items: z.array(connectorSchema) });

export const connectorRunSchema = z.object({
  id: z.string(),
  trigger: z.enum(['schedule', 'manual']),
  status: z.enum(['ok', 'empty', 'error']),
  httpStatus: z.number().int().nullable(),
  rows: z.number().int(),
  batchId: z.string().nullable(),
  error: z.string().nullable(),
  startedAt: z.string(),
  finishedAt: z.string(),
});
export const connectorRunListSchema = z.object({ items: z.array(connectorRunSchema) });

/** Lecture d'essai : rien n'est importé, la correspondance proposée peut être enregistrée. */
export const connectorTestSchema = z.object({
  httpStatus: z.number().int(),
  rowsTotal: z.number().int(),
  headers: z.array(z.string()),
  preview: z.array(z.array(z.string())),
  fields: z.array(importFieldSchema),
  suggestedMapping: z.record(z.string(), z.string()),
});
